import './AnalyticsCard.css';
import React, { useState } from 'react';
import { clsx } from 'clsx';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';

interface AnalyticsCardProps {
  toolTipDescription?: React.ReactNode;
  icon?: React.ReactNode;
  metric?: React.ReactNode;
  // Percentage change against the previous period; null hides the indicator.
  trend?: number | null;
  className?: string;
  metricRow?: boolean;
  title: React.ReactNode;
  // For metrics where lower is better, so a drop shows green.
  reverseIndicator?: boolean;
  color?: string;
}

// Up/down trend arrow; green means good, so reversed metrics swap the colours.
const ChangeIcons = ({
  trend,
  reverseIndicator,
}: {
  trend: number | null;
  reverseIndicator: boolean;
}) => {
  return reverseIndicator ? (
    <>
      {trend > 0 && <path d="M8.5 12L4.60289 5.25H12.3971L8.5 12Z" fill="#C11A45" />}{' '}
      {trend < 0 && <path d="M8.5 4L12.3971 10.75H4.60289L8.5 4Z" fill="#389D61" />}
    </>
  ) : (
    <>
      {trend > 0 && <path d="M8.5 4L12.3971 10.75H4.60289L8.5 4Z" fill="#389D61" />}{' '}
      {trend < 0 && <path d="M8.5 12L4.60289 5.25H12.3971L8.5 12Z" fill="#C11A45" />}
    </>
  );
};

export const AnalyticsCard = ({
  toolTipDescription = null,
  icon = null,
  metric = null,
  trend = null,
  className = '',
  metricRow = false,
  title,
  reverseIndicator = false,
  color = '',
}: AnalyticsCardProps) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const num = Number.parseFloat(String(trend));
  const displayPercentage = Number.isInteger(num) ? num : num.toFixed(1);

  return (
    <div
      className={clsx(
        'flex flex-col relative size-full p-4 rounded-sm-5 border border-border_subtle',
        className,
      )}
    >
      <div
        className="absolute left-0 top-0 h-full w-1.5 rounded-l-sm-5"
        style={{ backgroundColor: color }}
      ></div>
      <div className="flex flex-row size-full justify-between items-center">
        <div className="flex flex-row gap-[8px] items-center w-full">
          <div className="text-xs md:text-[0.875rem] font-semibold text-ebony text-nowrap ">
            {title}
          </div>
          {toolTipDescription && (
            <div className="cursor-pointer">
              <TooltipProvider>
                <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
                  <TooltipTrigger asChild>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      className="info-tooltip-icon"
                      onClick={() => {
                        setTooltipOpen(true);
                      }}
                    >
                      <path
                        d="M9.9999 13.3332V9.99984M9.9999 6.6665H10.0082M18.3332 9.99984C18.3332 14.6022 14.6022 18.3332 9.9999 18.3332C5.39752 18.3332 1.66656 14.6022 1.66656 9.99984C1.66656 5.39746 5.39752 1.6665 9.9999 1.6665C14.6022 1.6665 18.3332 5.39746 18.3332 9.99984Z"
                        stroke="#858FA0"
                        strokeWidth="1.67"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-2xl text-[0.75rem] text-[rgba(5, 5, 13, 0.50)] select-none">
                    {toolTipDescription}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
          {metricRow && (
            <div className="flex flex-row size-full items-center gap-[8px]">
              {metric && (
                <div className="flex text-lg font-bold text-ebony items-center">{metric}</div>
              )}
              {trend !== undefined && trend !== null && (
                <div
                  className={clsx(
                    'text-xs font-semibold flex flex-row',
                    trend > 0 && (reverseIndicator ? 'text-[#C11A45]' : 'text-[#389D61]'),
                    trend < 0 && (reverseIndicator ? 'text-[#389D61]' : 'text-[#C11A45]'),
                  )}
                >
                  <div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="17"
                      height="16"
                      viewBox="0 0 17 16"
                      fill="none"
                    >
                      <ChangeIcons trend={trend} reverseIndicator={reverseIndicator} />
                      {trend === 0 && (
                        <path
                          d="M8.5 4L12.3971 10.75H4.60289L8.5 4Z"
                          fill="#05050D"
                          fillOpacity="0.3"
                        />
                      )}
                    </svg>
                  </div>
                  {displayPercentage}%
                </div>
              )}
            </div>
          )}
        </div>
        {icon}
      </div>
      {!metricRow && (
        <div className="flex flex-row size-full items-center gap-[8px]">
          {metric && <div className="flex text-lg font-bold text-ebony items-center">{metric}</div>}
          {trend !== undefined && trend !== null && (
            <div
              className={clsx(
                'text-xs font-semibold flex flex-row',
                trend > 0 && (reverseIndicator ? 'text-[#C11A45]' : 'text-[#389D61]'),
                trend < 0 && (reverseIndicator ? 'text-[#389D61]' : 'text-[#C11A45]'),
              )}
            >
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="17"
                  height="16"
                  viewBox="0 0 17 16"
                  fill="none"
                >
                  <ChangeIcons trend={trend} reverseIndicator={reverseIndicator} />
                  {trend === 0 && (
                    <path
                      d="M8.5 4L12.3971 10.75H4.60289L8.5 4Z"
                      fill="#05050D"
                      fillOpacity="0.3"
                    />
                  )}
                </svg>
              </div>
              {displayPercentage}%
            </div>
          )}
        </div>
      )}
    </div>
  );
};
